import { AccordionDetails, CSSObject, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link } from 'react-router-dom';

import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import SwapIcon from '@/assets/icons/swap.svg?react';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { NfTransactionDetailsResponse } from '@/types/simulation';
import { AsAnyActionArg } from '@/utility/reducers';

interface QuoteWidgetState {
  readonly fromAmount: number;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number;
  readonly toCurrency: CurrencyCodeType;
}

enum QuoteWidgetActionType {
  swapCurrencies = 'swapCurrencies',
  setFromAmount = 'setFromAmount',
  setToAmount = 'setToAmount',
}

interface SwapCurrenciesAction {
  readonly type: QuoteWidgetActionType.swapCurrencies;
}

interface SetFromAmountAction {
  readonly type: QuoteWidgetActionType.setFromAmount;
  readonly amount: number;
}

interface SetToAmountAction {
  readonly type: QuoteWidgetActionType.setToAmount;
  readonly amount: number;
}

type QuoteWidgetAction = SwapCurrenciesAction | SetFromAmountAction | SetToAmountAction;

const reducer = (state: QuoteWidgetState, action: QuoteWidgetAction): QuoteWidgetState => {
  switch (action.type) {
    case QuoteWidgetActionType.swapCurrencies:
      return {
        ...state,
        fromAmount: state.toAmount,
        toAmount: state.fromAmount,
        fromCurrency: state.toCurrency,
        toCurrency: state.fromCurrency,
      };
    case QuoteWidgetActionType.setFromAmount:
      return {
        ...state,
        fromAmount: action.amount,
      };
    case QuoteWidgetActionType.setToAmount:
      return {
        ...state,
        toAmount: action.amount,
      };
    default:
      return state;
  }
};

const initialState: QuoteWidgetState = {
  fromAmount: 5.43,
  fromCurrency: CurrencyCodeType.BRL,
  toAmount: 1,
  toCurrency: CurrencyCodeType.USD,
};

export const QuoteWidget: React.FC = (): React.ReactElement => {
  const [state, dispatch] = React.useReducer<QuoteWidgetState, AsAnyActionArg<QuoteWidgetAction>>(
    reducer,
    initialState,
  );
  const { fromCurrency, toCurrency, fromAmount, toAmount } = state;
  const tr = useTranslator();

  const onSwapCurrencies = React.useCallback((): void => {
    dispatch({ type: QuoteWidgetActionType.swapCurrencies });
  }, []);

  return (
    <Box padding={3}>
      <Typography variant="h7">{tr('CONVERSION_QUOTE')}</Typography>
      <Box sx={styles.inputsContainer}>
        <AmountAndCurrencyWidget
          label={tr('FROM')}
          size="small"
          currency={fromCurrency}
          amount={5.43}
          readOnly={true}
          currencies={[]}
        />

        <AmountAndCurrencyWidget
          label={tr('TO')}
          size="small"
          currency={toCurrency}
          amount={1}
          readOnly={true}
          currencies={[]}
        />

        {/* Goes here for a natural and valid stacking order */}
        <IconButton sx={styles.swapButton} onClick={onSwapCurrencies}>
          <SwapIcon />
        </IconButton>
      </Box>

      <Box my={3}>
        <StyledAccordion disabled={false}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography fontSize={14} fontWeight={400} color="text.primary">
              $5.43 BRL = 1 USDC
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TransactionDetails
              transactionDetails={fakeDetails}
              fromAmount={fromAmount}
              fromCurrency={fromCurrency}
              toAmount={toAmount}
              toCurrency={toCurrency}
              textVariant="body1"
            />
          </AccordionDetails>
        </StyledAccordion>
      </Box>
      <Button variant="contained" component={Link} to="deposit" fullWidth={true}>
        {tr('ADD_DIGITAL_DOLLARS')}
      </Button>
    </Box>
  );
};

const fakeDetails: NfTransactionDetailsResponse = {
  conversionDetails: {
    effectiveTransactionValue: 100,
    marketExchangeRate: 5.43,
    clientRate: 5.3,
    calizaRate: 0,
    amountToBeConverted: {
      value: 0,
      currencyCode: CurrencyCodeType.USD,
    },
  },
  feeDetails: {
    taxes: null,
    totalFees: {
      value: 0.05,
      currencyCode: CurrencyCodeType.USD,
    },
    integratorFee: {
      value: 0,
      currencyCode: CurrencyCodeType.USD,
    },
    fixedFees: [],
    oldFees: [],
    percentageFees: [],
    spreadFees: [],
    vet: '0.0000',
  },
  expectedSettlementTimeDetails: null,
};

const styles: Record<string, SystemStyleObject> = {
  inputsContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    mt: 3,
  },
  swapButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    padding: 0,
    width: 40,
    height: 40,
    mt: '-20px',
    ml: '-20px',
  },
};

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 8,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 8,
    },
  }),
);
